'use client';
import {FC, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {createUseStyles} from 'react-jss';
import {colorPalette} from 'styles/colors';
import {CssBreakpoints} from 'styles/responsive';
import {StringUtils} from 'common/utils/string-utils';
import {useLang} from 'features/i18n/use-lang';

const useStyles = createUseStyles(
  theme => ({
    '@global': {
      '#maincontent': {
        paddingBottom: '0',
      },
    },

    header: {
      '@media screen': {
        marginTop: '2rem',
        marginBottom: '2rem',
      },
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      borderBottom: `1px solid ${colorPalette.grey20}`,
      alignItems: 'last baseline',
      '@media print': {
        borderBottomWidth: '1pt',
      },
    },
    headingText: {
      color: theme.colors.text.heading,
      fontSize: '2rem',
      fontWeight: 'normal',
      margin: 0,
      marginBottom: '1rem',
      '@media print': {
        fontSize: '1.5rem',
        marginBottom: '0',
      },
      [`@media (max-width: ${CssBreakpoints.MD}px)`]: {
        fontSize: '1.5rem',
      },
      [`@media (max-width: ${CssBreakpoints.XS}px)`]: {
        fontSize: '1.2rem',
      },
    },
    subtitle: {
      paddingTop: '1rem',
      fontSize: '1.25rem',
      '@media print': {
        fontSize: '1rem',
        paddingTop: '0.5rem',
      },
    },
    subtitleLabel: {
      fontWeight: 600,
    },
    oppslagstidspunkt: {
      fontSize: '1rem',
      fontWeight: 'normal',
      color: theme.colors.text.heading,
      lineHeight: '1.25em',
      marginTop: '1rem',
      marginBottom: '0.5rem',
    },
    printBtnMobile: {
      [`@media (max-width: ${CssBreakpoints.XS}px)`]: {
        marginLeft: '0',
      },
    },
    status: {
      color: theme.colors.info.fg,
      margin: '1rem auto',
    },
    statusIcon: {
      marginRight: '0.5rem',
    },
    statusLabel: {
      fontWeight: 600,
    },
    statusDetails: {
      fontWeight: '600',
      fontSize: '1rem',
    },
  }),
  {name: 'ResultsHeader'}
);

interface ResultHeaderProps {
  id: string;
  title: string;
  printTitle: string;
  subtitle?: {label: string; value: string};
  oppslagstidspunktLabel: string;
  oppslagstidspunkt: Date;
  buttons: ReactNode[];
  status?: {
    icon: ReactNode;
    text: string;
    details: string;
  };
}

export const ResultHeader: FC<ResultHeaderProps> = ({
  id,
  title,
  subtitle,
  printTitle,
  status,
  oppslagstidspunkt,
  oppslagstidspunktLabel,
  buttons,
}) => {
  const styles = useStyles();
  const {t} = useTranslation(['common']);
  const appLang = useLang();

  return (
    <header id='maincontent' data-testid='result-header' className={styles.header}>
      <div className={styles.heading}>
        <h1 id={`${id}_title`} className={styles.headingText}>
          <span className='no-print'>{title}</span>
          <span className='print-only'>{printTitle}</span>
        </h1>
      </div>
      {subtitle && (
        <div data-testid='subtitle' className={styles.subtitle}>
          <span className={styles.subtitleLabel}>{subtitle.label}:</span>
          <span> {subtitle.value}</span>
        </div>
      )}
      {status && (
        <>
          <div className={styles.status}>
            <span className={styles.statusIcon}>{status.icon}</span>
            <span className={styles.statusLabel}>Status:</span> {status.text}
          </div>
          <p className={styles.statusDetails}>{status.details}</p>
        </>
      )}
      <h2 data-testid='oppslagstidspunkt' className={styles.oppslagstidspunkt}>
        {oppslagstidspunktLabel}, {StringUtils.formatDateTime(oppslagstidspunkt, appLang, t)}
      </h2>
      {...buttons}
    </header>
  );
};
